/**
 * Workpackage-instellingen, te openen vanuit het Repository-menu voor het
 * workpackage waarin je werkt: naam, beschrijving en wat een AI hier mag
 * schrijven (migratie 0062).
 *
 * Iedereen ziet de instellingen, zodat elke modelleur weet wat een AI hier
 * mag; wijzigen kan een workspace-admin of een team met schrijfrecht
 * (`myAccess` uit de server). Het Base-workpackage heeft een vaste naam en
 * beschrijving; daar is alleen de AI-stand in te stellen.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { type AiWriteMode, ApiError, api, type WorkPackageDTO } from "../api/client";
import { AiWriteChoice } from "../settings/AiWriteSetting";

function explain(err: unknown): string {
  if (err instanceof ApiError) return err.body?.detail ?? err.body?.title ?? `HTTP ${err.status}`;
  return err instanceof Error ? err.message : String(err);
}

export function WorkpackageSettingsDialog({
  workspaceId,
  workpackageId,
  onClose,
}: {
  workspaceId: string;
  workpackageId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [wp, setWp] = useState<WorkPackageDTO | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [aiWrite, setAiWrite] = useState<AiWriteMode>("ai_plateau");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    if (!workpackageId) return;
    let cancelled = false;
    api
      .getWorkpackage(workspaceId, workpackageId)
      .then((res) => {
        if (cancelled) return;
        setWp(res);
        setName(res.name);
        setDescription(res.description ?? "");
        setAiWrite(res.aiWrite ?? "ai_plateau");
      })
      .catch((err) => !cancelled && setError(explain(err)));
    return () => {
      cancelled = true;
    };
  }, [workspaceId, workpackageId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const canEdit = wp?.myAccess === "admin" || wp?.myAccess === "write";
  const isBase = wp?.isBase === true;
  const dirty =
    wp !== null &&
    ((!isBase && (name.trim() !== wp.name || description.trim() !== (wp.description ?? ""))) ||
      aiWrite !== (wp.aiWrite ?? "ai_plateau"));

  const save = async () => {
    if (!wp || !canEdit || !dirty) return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const updated = await api.patchWorkpackage(workspaceId, wp.id, {
        ...(!isBase && name.trim() !== wp.name ? { name: name.trim() } : {}),
        ...(!isBase && description.trim() !== (wp.description ?? "")
          ? { description: description.trim() || null }
          : {}),
        ...(aiWrite !== (wp.aiWrite ?? "ai_plateau") ? { ai_write: aiWrite } : {}),
      });
      setWp({ ...updated, ...(wp.myAccess ? { myAccess: wp.myAccess } : {}) });
      setInfo(t("wpSettings.saved"));
      window.dispatchEvent(new CustomEvent("ae:workpackages-changed"));
    } catch (err) {
      setError(explain(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ledger fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/30">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t("wpSettings.title")}
        className="flex max-h-[90vh] w-[520px] max-w-[95vw] flex-col gap-4 overflow-y-auto rounded-lg border border-(--ld-control) bg-(--ld-surface) p-5 shadow-xl"
      >
        <h2 className="font-semibold text-(--ld-text)">
          {t("wpSettings.title")}
          {wp ? ` — ${wp.name}` : ""}
        </h2>
        {!workpackageId && (
          <p className="text-(--ld-muted) text-sm">{t("wpSettings.noWorkpackage")}</p>
        )}
        {workpackageId && !wp && !error && (
          <p className="text-(--ld-muted) text-sm">{t("wpSettings.loading")}</p>
        )}
        {error && (
          <div className="rounded border border-(--ld-control) bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative-ink) text-sm">
            {error}
          </div>
        )}
        {info && (
          <div className="rounded border border-transparent bg-(--ld-positive-bg) px-3 py-2 text-(--ld-positive-ink) text-sm">
            {info}
          </div>
        )}
        {wp && (
          <>
            {!canEdit && (
              <p className="rounded border border-(--ld-border) bg-(--ld-raised) px-3 py-2 text-(--ld-muted) text-xs">
                {t("wpSettings.readOnly")}
              </p>
            )}
            {isBase && <p className="text-(--ld-muted) text-xs">{t("wpSettings.baseReadOnly")}</p>}
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-(--ld-text)">{t("wpSettings.nameLabel")}</span>
              <input
                className="rounded border border-(--ld-control) px-3 py-1.5 text-sm disabled:bg-(--ld-raised) disabled:text-(--ld-muted)"
                value={name}
                disabled={!canEdit || isBase || busy}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-(--ld-text)">
                {t("wpSettings.descriptionLabel")}
              </span>
              <textarea
                className="min-h-[70px] rounded border border-(--ld-control) px-3 py-1.5 text-sm disabled:bg-(--ld-raised) disabled:text-(--ld-muted)"
                value={description}
                disabled={!canEdit || isBase || busy}
                onChange={(e) => setDescription(e.target.value)}
              />
            </label>
            <AiWriteChoice value={aiWrite} onChange={setAiWrite} readOnly={!canEdit || busy} />
          </>
        )}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1.5 text-(--ld-text) text-sm hover:bg-(--ld-raised)"
          >
            {t("wpSettings.close")}
          </button>
          {canEdit && (
            <button
              type="button"
              onClick={() => void save()}
              disabled={busy || !dirty || (!isBase && !name.trim())}
              className="rounded bg-(--ld-primary) px-3 py-1.5 font-medium text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
            >
              {t("wpSettings.save")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
